// Utility-estimated readings — the source's own word that a reading was not measured.
//
// A utility fills a missed read with an estimate and usually says so: Green
// Button carries ReadingQuality on each reading, and some CSV and Excel
// exports carry an "Estimated" flag or a read-quality code column. TrueLoad
// reads that marker only where the file states it plainly. A column has to be
// named for it ("estimated", "estimate", "quality") and a cell has to say it
// in a recognised word or code. Anything else is not guessed at.
//
// Observational only: nothing here changes a reading or a column choice. The
// timestamps feed the utility_estimated plausibility check (suspectPeaks.ts).

import { headerWords, parseFlexibleTimestamp } from '../fileParser';
import type { ColumnAssignment } from './types';

/**
 * ESPI QualityOfReading codes that mean the value was estimated: 8 "estimated
 * using reference day", 9 "estimated using linear interpolation". Not 7
 * (manually edited), 12 (projected) or any validation code.
 */
export const GREEN_BUTTON_ESTIMATED_QUALITY = new Set([8, 9]);

const ESTIMATED_HEADER = /\b(estimated|estimate|estimation)\b/;
const QUALITY_HEADER = /\bquality\b/;
/** A cell under an "estimated" header that says yes. */
const YES_CELL = /^(y|yes|true|t|1|e|est|estimated|x)$/i;
/** A cell under a "quality" header that says estimated. */
const ESTIMATED_CODE_CELL = /^(e|est|estimated?|estimation)$/i;

/** The column that states estimation, and how its cells read; null when none is named for it. */
function estimatedColumn(
  headerRow: (string | null)[] | null,
  assignment: ColumnAssignment
): { index: number; cell: RegExp } | null {
  if (!headerRow) return null;
  const taken = new Set([assignment.timestampIndex, assignment.demandIndex, assignment.unitColumnIndex ?? -1]);
  let quality: number | null = null;
  for (let i = 0; i < headerRow.length; i++) {
    const h = headerRow[i];
    if (!h || taken.has(i)) continue;
    const words = headerWords(h);
    if (ESTIMATED_HEADER.test(words)) return { index: i, cell: YES_CELL };
    if (quality === null && QUALITY_HEADER.test(words)) quality = i;
  }
  return quality === null ? null : { index: quality, cell: ESTIMATED_CODE_CELL };
}

/**
 * Timestamps of the rows the file marks as estimated, in the extractor's own
 * form (ISO from the same timestamp parse), so they match the data points.
 * Empty when no column is named for it.
 */
export function estimatedTimestampsFromRows(
  rows: unknown[][],
  headerRow: (string | null)[] | null,
  assignment: ColumnAssignment,
  dataStartRow: number
): string[] {
  const col = estimatedColumn(headerRow, assignment);
  if (!col) return [];
  const out: string[] = [];
  for (let i = dataStartRow; i < rows.length; i++) {
    const row = rows[i] as unknown[];
    const cell = row?.[col.index];
    if (cell === null || cell === undefined) continue;
    if (!col.cell.test(String(cell).trim())) continue;
    const raw = row[assignment.timestampIndex];
    const ts = raw instanceof Date && !isNaN(raw.getTime())
      ? raw
      : raw !== null && raw !== undefined ? parseFlexibleTimestamp(String(raw)) : null;
    if (ts) out.push(ts.toISOString());
  }
  return out;
}
